package com.sddamico.draftmachine.pages.draft

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleH2
import com.sddamico.draftmachine.models.ConsumeMagicLinkRequest
import com.sddamico.draftmachine.models.ConsumeMagicLinkResponse
import com.sddamico.draftmachine.models.ReplaceMagicLinkRequest
import com.sddamico.draftmachine.models.SuccessResponse
import com.sddamico.draftmachine.util.UNREACHABLE_MESSAGE
import com.sddamico.draftmachine.util.currentPushEndpoint
import com.sddamico.draftmachine.util.queryParam
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

internal sealed interface MagicState {
  data object Idle : MagicState
  data object Consuming : MagicState
  data object Expired : MagicState
  data object Replacing : MagicState
  data object Sent : MagicState
  data class ReplaceFailed(val message: String) : MagicState
  data class Invalid(val message: String) : MagicState
  /** The link belongs to [linkEmail], but a different account is signed in on this browser. */
  data class SwitchPrompt(val linkEmail: String) : MagicState
  data class Switching(val linkEmail: String) : MagicState
}

/**
 * The `?magic=` single-use draft-link flow shared by the draft room, board and settings pages: holds
 * the token (stripped from the URL at mount), consumes it into a session, and drives the recovery
 * screen when the link is dead. Render the recovery UI via [MagicLinkGate].
 *
 * A signed-out visitor is signed in by [consume]. A signed-in visitor goes through [settle], which
 * never changes the session on its own: a link is whatever its sender put in the URL, so landing on
 * one must not quietly move someone into another person's account.
 */
internal class MagicLink(val token: String?, private val scope: CoroutineScope) {
  var state by mutableStateOf<MagicState>(MagicState.Idle)
    private set
  private var settled = false

  val available: Boolean get() = token != null

  private suspend fun post(token: String, confirmSwitch: Boolean): ConsumeMagicLinkResponse = apiPost(
    "/api/auth/magic-link-consume",
    apiJson.encodeToString(
      ConsumeMagicLinkRequest.serializer(),
      ConsumeMagicLinkRequest(token, confirmSwitch, if (confirmSwitch) currentPushEndpoint() else null),
    ),
    ConsumeMagicLinkResponse.serializer(),
  )

  private fun signIn(token: String, confirmSwitch: Boolean) {
    scope.launch {
      try {
        val response = post(token, confirmSwitch)
        if (response.switchRequired) {
          state = MagicState.SwitchPrompt(response.email)
        } else {
          window.location.replace(response.destination)
        }
      } catch (e: ApiException) {
        state = if (e.status == 410) MagicState.Expired else MagicState.Invalid(e.errorMessage)
      } catch (e: Throwable) {
        state = MagicState.Invalid(e.message ?: UNREACHABLE_MESSAGE)
      }
    }
  }

  fun consume() {
    val token = token ?: return
    if (state is MagicState.Consuming) return
    state = MagicState.Consuming
    signIn(token, confirmSwitch = false)
  }

  /**
   * For a visitor who already has a session. The server burns the token when it belongs to the
   * signed-in account; when it belongs to someone else nothing happens until they answer the prompt.
   */
  fun settle() {
    val token = token ?: return
    if (settled) return
    settled = true
    scope.launch {
      try {
        val response = post(token, confirmSwitch = false)
        if (response.switchRequired) state = MagicState.SwitchPrompt(response.email)
      } catch (_: Throwable) {
        // A dead link costs a visitor who is already signed in nothing.
      }
    }
  }

  fun confirmSwitch() {
    val token = token ?: return
    val prompt = state as? MagicState.SwitchPrompt ?: return
    state = MagicState.Switching(prompt.linkEmail)
    signIn(token, confirmSwitch = true)
  }

  fun dismissSwitch() {
    if (state is MagicState.SwitchPrompt) state = MagicState.Idle
  }

  fun replace() {
    val token = token ?: return
    state = MagicState.Replacing
    scope.launch {
      try {
        apiPost(
          "/api/auth/magic-link-replace",
          apiJson.encodeToString(ReplaceMagicLinkRequest.serializer(), ReplaceMagicLinkRequest(token)),
          SuccessResponse.serializer(),
        )
        state = MagicState.Sent
      } catch (e: Throwable) {
        state = MagicState.ReplaceFailed(e.message ?: UNREACHABLE_MESSAGE)
      }
    }
  }
}

@Composable
internal fun rememberMagicLink(scope: CoroutineScope): MagicLink = remember {
  val token = queryParam("magic")
  if (token != null) window.history.replaceState(null, "", window.location.pathname)
  MagicLink(token, scope)
}

/**
 * Renders the full-screen recovery UI when the magic link turned out to be dead, or the
 * switch-account prompt when it belongs to a different account than the one signed in.
 * @return true when it rendered — the caller should return without composing the page body.
 */
@Composable
internal fun MagicLinkGate(magic: MagicLink, onLogin: () -> Unit): Boolean {
  when (val state = magic.state) {
    MagicState.Idle, MagicState.Consuming -> return false
    is MagicState.SwitchPrompt -> MagicLinkSwitchPrompt(state.linkEmail, false, magic::confirmSwitch, magic::dismissSwitch)
    is MagicState.Switching -> MagicLinkSwitchPrompt(state.linkEmail, true, magic::confirmSwitch, magic::dismissSwitch)
    else -> MagicLinkRecovery(state = state, onReplace = magic::replace, onLogin = onLogin)
  }
  return true
}

@Composable
private fun MagicLinkSwitchPrompt(linkEmail: String, switching: Boolean, onSwitch: () -> Unit, onStay: () -> Unit) {
  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column)
      .justifyContent(JustifyContent.Center).alignItems(AlignItems.Center),
  ) {
    Div(
      attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).alignItems(AlignItems.Center)
        .gap(14.px).padding(32.px).styleModifier { property("text-align", "center") }.toAttrs(),
    ) {
      Icon("user-switch", IconWeight.Bold, Modifier.fontSize(42.px).color(DmColor.brass500))
      SpanText("Switch accounts?", Modifier.roleH2())
      SpanText(
        "This link signs in as $linkEmail, but this browser is signed in to a different account.",
        Modifier.roleBody().color(DmColor.textMuted).styleModifier { property("overflow-wrap", "anywhere") },
      )
      Button(
        "Switch accounts", variant = ButtonVariant.Primary, icon = "sign-in",
        loading = switching, onClick = onSwitch,
      )
      Button("Stay signed in", variant = ButtonVariant.Secondary, disabled = switching, onClick = onStay)
    }
  }
}

@Composable
internal fun MagicLinkRecovery(state: MagicState, onReplace: () -> Unit, onLogin: () -> Unit) {
  val expired = state is MagicState.Expired || state is MagicState.Replacing ||
    state is MagicState.Sent || state is MagicState.ReplaceFailed
  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column)
      .justifyContent(JustifyContent.Center).alignItems(AlignItems.Center),
  ) {
    Div(
      attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).alignItems(AlignItems.Center)
        .gap(14.px).padding(32.px).styleModifier { property("text-align", "center") }.toAttrs(),
    ) {
      Icon(if (expired) "clock-countdown" else "link-break", IconWeight.Bold, Modifier.fontSize(42.px).color(DmColor.brass500))
      SpanText(if (expired) "This link has expired" else "This link is unavailable", Modifier.roleH2())
      SpanText(
        when (state) {
          MagicState.Sent -> "A fresh link is on its way. Check the inbox where you received the draft notice."
          is MagicState.ReplaceFailed -> state.message
          is MagicState.Invalid -> state.message
          else -> "Draft links are single-use. Request a fresh one or use the normal login flow."
        },
        Modifier.roleBody().color(DmColor.textMuted),
      )
      if (expired && state !is MagicState.Sent) {
        Button(
          "Send me a new link", variant = ButtonVariant.Primary, icon = "paper-plane-tilt",
          loading = state is MagicState.Replacing, onClick = onReplace,
        )
      }
      Button("Go to normal login", variant = ButtonVariant.Secondary, icon = "sign-in", onClick = onLogin)
    }
  }
}
